/* eslint-disable no-magic-numbers */
/**
 * Drawing utilities for canvas rendering
 * Extracted from canvasUtils.ts to reduce file size and improve organization
 */

import { UnreachableCaseError } from 'ts-essentials';
import type {
  PointStyle,
  CanvasElements,
  MapPoint,
  TrajectoryStyle,
  ConvertToScreenPositionFn,
  ScreenPositionContext,
} from '../types/interfaces';
import { MAP_CONSTANTS } from '../types/constants';

/**
 * Mathematical constant: Square root of 2 (√2 ≈ 1.414)
 * Used for geometric calculations, particularly for inscribing squares in circles
 * where the diagonal of the square equals the diameter of the circle
 */
const SQRT_2 = Math.SQRT2;

// Constants for magic numbers
const SCALE_FACTOR = 1.2;
const DIVISION_FACTOR = 2;
const OPACITY_FACTOR = 0.8;
const TWO_PI = 2 * Math.PI;

// Cache for loaded images
const imageCache = new Map<string, HTMLImageElement>();

// Pre-rasterized icon bitmaps keyed by src|physicalSizePx|tinted
const iconBitmapCache = new Map<string, HTMLCanvasElement>();

const pendingRedrawCallbacks = new Set<() => void>();
let redrawScheduled = false;

function scheduleRedraw(callback: () => void): void {
  pendingRedrawCallbacks.add(callback);
  if (!redrawScheduled) {
    redrawScheduled = true;
    requestAnimationFrame(() => {
      redrawScheduled = false;
      for (const cb of pendingRedrawCallbacks) {
        cb();
      }
      pendingRedrawCallbacks.clear();
    });
  }
}

/**
 * Load and cache an image
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  if (imageCache.has(src)) {
    return Promise.resolve(imageCache.get(src)!);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      imageCache.set(src, img);
      resolve(img);
    };
    img.onerror = reject;
    img.src = src;
  });
}

function getIconBitmapCacheKey(
  src: string,
  sizePx: number,
  tinted: boolean
): string {
  return `${src}|${sizePx}|${tinted ? '1' : '0'}`;
}

function buildIconBitmap(
  img: HTMLImageElement,
  sizePx: number,
  tinted: boolean
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  const offCtx = canvas.getContext('2d');
  if (offCtx) {
    if (tinted) {
      offCtx.filter = 'brightness(0) invert(1)';
    }
    offCtx.drawImage(img, 0, 0, sizePx, sizePx);
  }
  return canvas;
}

function getIconBitmap(
  src: string,
  iconSize: number,
  dpr: number,
  tinted: boolean,
  onReady?: () => void
): HTMLCanvasElement | undefined {
  const sizePx = Math.round(iconSize * dpr);
  const key = getIconBitmapCacheKey(src, sizePx, tinted);
  const cached = iconBitmapCache.get(key);
  if (cached) {
    return cached;
  }

  const img = imageCache.get(src);
  if (img) {
    const bitmap = buildIconBitmap(img, sizePx, tinted);
    iconBitmapCache.set(key, bitmap);
    return bitmap;
  }

  void loadImage(src)
    .then(() => {
      if (onReady) {
        scheduleRedraw(onReady);
      }
    })
    .catch(() => {
      // Icon failed to load
    });
  return undefined;
}

function drawIconBitmap(
  ctx: CanvasRenderingContext2D,
  bitmap: HTMLCanvasElement,
  position: { x: number; y: number },
  dpr: number
): void {
  const sizeCss = bitmap.width / dpr;
  const halfCss = sizeCss / DIVISION_FACTOR;
  const topLeftDeviceX = Math.round((position.x - halfCss) * dpr);
  const topLeftDeviceY = Math.round((position.y - halfCss) * dpr);
  ctx.drawImage(
    bitmap,
    topLeftDeviceX / dpr,
    topLeftDeviceY / dpr,
    sizeCss,
    sizeCss
  );
}

/**
 * Preload flag icons to ensure they're available for rendering
 */
export function preloadFlagIcons(): void {
  // Import flag icons and preload them
  void import('../assets/start-flag.svg').then((module) => {
    void loadImage(module.default).catch(() => {
      // Silently handle loading errors
    });
  });

  void import('../assets/end-flag.svg').then((module) => {
    void loadImage(module.default).catch(() => {
      // Silently handle loading errors
    });
  });
}

/**
 * Draw a styled point on canvas
 * @param onImageLoaded - Optional callback invoked (via requestAnimationFrame
 *   coalescing) after an uncached icon finishes loading, so the caller can
 *   schedule a canvas redraw.
 */
export function drawStyledPoint(
  ctx: CanvasRenderingContext2D,
  position: { x: number; y: number },
  style: PointStyle,
  onImageLoaded?: () => void
): void {
  ctx.save();

  if (style.shadowOffset || style.shadowColor || style.shadowBlur) {
    ctx.shadowOffsetX = style.shadowOffset?.x || 0;
    ctx.shadowOffsetY = style.shadowOffset?.y || 0;
    ctx.shadowColor = style.shadowColor || 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = style.shadowBlur || 0;
  }

  if (style.iconOnly && style.icon) {
    const iconSize = style.size * SCALE_FACTOR;
    const dpr = window.devicePixelRatio || 1;
    const bitmap = getIconBitmap(
      style.icon,
      iconSize,
      dpr,
      false,
      onImageLoaded
    );
    if (bitmap) {
      drawIconBitmap(ctx, bitmap, position, dpr);
    }
    ctx.restore();
    return;
  }

  drawPointShape(ctx, position, style);

  if (style.icon) {
    const iconSize = style.size * SCALE_FACTOR;
    const dpr = window.devicePixelRatio || 1;
    const bitmap = getIconBitmap(
      style.icon,
      iconSize,
      dpr,
      true,
      onImageLoaded
    );
    if (bitmap) {
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      drawIconBitmap(ctx, bitmap, position, dpr);
    }
  }

  ctx.restore();
}

/**
 * Draw point using shape (circle, square, etc.)
 */
function drawPointShape(
  ctx: CanvasRenderingContext2D,
  position: { x: number; y: number },
  style: PointStyle
): void {
  ctx.fillStyle = style.color;
  ctx.strokeStyle = style.borderColor || '#FFFFFF';
  ctx.lineWidth = style.borderWidth || 1;

  // Note: style.size is the radius, not diameter (to match original Maya implementation)
  const radius = style.size;

  switch (style.shape) {
    case 'circle':
      ctx.beginPath();
      ctx.arc(position.x, position.y, radius, 0, TWO_PI);
      ctx.fill();
      if (style.borderWidth) ctx.stroke();
      break;

    case 'square': {
      // For square, use radius to create a square that fits inside a circle of that radius
      const squareSize = radius * SQRT_2; // Diagonal of square = diameter of circle
      const halfSquare = squareSize / DIVISION_FACTOR;
      ctx.fillRect(
        position.x - halfSquare,
        position.y - halfSquare,
        squareSize,
        squareSize
      );
      if (style.borderWidth) {
        ctx.strokeRect(
          position.x - halfSquare,
          position.y - halfSquare,
          squareSize,
          squareSize
        );
      }
      break;
    }

    case 'diamond':
      ctx.beginPath();
      ctx.moveTo(position.x, position.y - radius);
      ctx.lineTo(position.x + radius, position.y);
      ctx.lineTo(position.x, position.y + radius);
      ctx.lineTo(position.x - radius, position.y);
      ctx.closePath();
      ctx.fill();
      if (style.borderWidth) ctx.stroke();
      break;

    case 'triangle':
      ctx.beginPath();
      ctx.moveTo(position.x, position.y - radius);
      ctx.lineTo(position.x + radius, position.y + radius);
      ctx.lineTo(position.x - radius, position.y + radius);
      ctx.closePath();
      ctx.fill();
      if (style.borderWidth) ctx.stroke();
      break;

    default:
      throw new UnreachableCaseError(style.shape);
  }
}

/**
 * Build a stable key for grouping points that share identical visual style.
 */
export function getPointStyleKey(style: PointStyle): string {
  return [
    style.color,
    style.borderColor ?? '',
    style.borderWidth ?? '',
    style.shape,
    style.size,
    style.icon ?? '',
    style.iconOnly ? '1' : '0',
    style.shadowColor ?? '',
    style.shadowBlur ?? '',
    style.shadowOffset?.x ?? '',
    style.shadowOffset?.y ?? '',
  ].join('|');
}

/**
 * Batch-draw point shapes sharing the same style in a single path fill/stroke.
 */
export function drawStyledPointsBatch(
  ctx: CanvasRenderingContext2D,
  positions: ReadonlyArray<{ x: number; y: number }>,
  style: PointStyle
): void {
  if (positions.length === 0) {
    return;
  }

  ctx.save();

  if (style.shadowOffset || style.shadowColor || style.shadowBlur) {
    ctx.shadowOffsetX = style.shadowOffset?.x || 0;
    ctx.shadowOffsetY = style.shadowOffset?.y || 0;
    ctx.shadowColor = style.shadowColor || 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = style.shadowBlur || 0;
  }

  ctx.fillStyle = style.color;
  ctx.strokeStyle = style.borderColor || '#FFFFFF';
  ctx.lineWidth = style.borderWidth || 1;

  const radius = style.size;

  switch (style.shape) {
    case 'circle':
      ctx.beginPath();
      for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        ctx.moveTo(position.x + radius, position.y);
        ctx.arc(position.x, position.y, radius, 0, TWO_PI);
      }
      ctx.fill();
      if (style.borderWidth) {
        ctx.stroke();
      }
      break;

    case 'square': {
      const squareSize = radius * SQRT_2;
      const halfSquare = squareSize / DIVISION_FACTOR;
      for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        ctx.fillRect(
          position.x - halfSquare,
          position.y - halfSquare,
          squareSize,
          squareSize
        );
        if (style.borderWidth) {
          ctx.strokeRect(
            position.x - halfSquare,
            position.y - halfSquare,
            squareSize,
            squareSize
          );
        }
      }
      break;
    }

    case 'diamond':
      ctx.beginPath();
      for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        ctx.moveTo(position.x, position.y - radius);
        ctx.lineTo(position.x + radius, position.y);
        ctx.lineTo(position.x, position.y + radius);
        ctx.lineTo(position.x - radius, position.y);
        ctx.closePath();
      }
      ctx.fill();
      if (style.borderWidth) {
        ctx.stroke();
      }
      break;

    case 'triangle':
      ctx.beginPath();
      for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        ctx.moveTo(position.x, position.y - radius);
        ctx.lineTo(position.x + radius, position.y + radius);
        ctx.lineTo(position.x - radius, position.y + radius);
        ctx.closePath();
      }
      ctx.fill();
      if (style.borderWidth) {
        ctx.stroke();
      }
      break;

    default:
      throw new UnreachableCaseError(style.shape);
  }

  ctx.restore();
}

/**
 * Draw point icons individually (cannot be batched with path fill).
 */
export function drawPointIcons(
  ctx: CanvasRenderingContext2D,
  positions: ReadonlyArray<{ x: number; y: number }>,
  style: PointStyle,
  onImageLoaded?: () => void
): void {
  if (!style.icon) {
    return;
  }

  const iconSize = style.size * SCALE_FACTOR;
  const dpr = window.devicePixelRatio || 1;
  const tinted = !style.iconOnly;
  const bitmap = getIconBitmap(
    style.icon,
    iconSize,
    dpr,
    tinted,
    onImageLoaded
  );
  if (!bitmap) {
    return;
  }

  for (let i = 0; i < positions.length; i++) {
    drawIconBitmap(ctx, bitmap, positions[i], dpr);
  }
}

/**
 * Draw connecting lines between map points in sequential order
 * @param convertToScreenPosition - Function to convert normalized coordinates to screen coordinates
 */
export function drawConnectingLines(
  ctx: CanvasRenderingContext2D,
  points: MapPoint[],
  canvasSize: { width: number; height: number },
  lineStyle: Partial<TrajectoryStyle>,
  convertToScreenPosition: ConvertToScreenPositionFn,
  zoom: number = 1,
  panOffset: { x: number; y: number } = { x: 0, y: 0 },
  imageElement?: HTMLImageElement,
  mapContainer?: HTMLElement,
  mainCanvas?: HTMLCanvasElement,
  screenContext?: ScreenPositionContext
): void {
  if (points.length < 2) return;

  ctx.save();
  ctx.strokeStyle = lineStyle.color || '#89C6ED';
  ctx.lineWidth = lineStyle.width || DIVISION_FACTOR;
  ctx.globalAlpha = lineStyle.opacity || OPACITY_FACTOR;
  ctx.lineCap = lineStyle.endCaps || 'round';
  ctx.lineJoin = lineStyle.joinStyle || 'round';

  if (lineStyle.dashPattern) {
    ctx.setLineDash(lineStyle.dashPattern);
  }

  ctx.beginPath();

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const screenPos = screenContext
      ? screenContext.projectNormalized(point.position2D)
      : convertToScreenPosition(
          point.position2D,
          canvasSize,
          zoom,
          panOffset,
          MAP_CONSTANTS.HEADER_HEIGHT,
          imageElement,
          mapContainer,
          mainCanvas
        );

    if (i === 0) {
      ctx.moveTo(screenPos.x, screenPos.y);
    } else {
      ctx.lineTo(screenPos.x, screenPos.y);
    }
  }

  ctx.stroke();
  ctx.restore();
}

/**
 * Update canvas size to match container with proper DPR scaling for HiDPI displays
 */
export function updateCanvasSize(
  canvasElements: CanvasElements,
  size: { width: number; height: number }
): void {
  const { mainCanvas, dynamicCanvas } = canvasElements;

  // Get the device pixel ratio, falling back to 1
  const dpr = window.devicePixelRatio || 1;

  const applySize = (canvas: HTMLCanvasElement): void => {
    canvas.style.width = `${String(size.width)}px`;
    canvas.style.height = `${String(size.height)}px`;

    canvas.width = size.width * dpr;
    canvas.height = size.height * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
  };

  if (mainCanvas) {
    applySize(mainCanvas);
  }

  if (dynamicCanvas) {
    applySize(dynamicCanvas);
  }
}

/** Y-offset for issue marker points (drawn above their position) */
export const ISSUE_MARKER_Y_OFFSET = -16;

const CLUSTER_PILL_HEIGHT = 14;
const CLUSTER_PILL_PADDING_X = 6;
const CLUSTER_PILL_OFFSET_FROM_ICON = 4;
const CLUSTER_PILL_FONT_SIZE = 10;

export interface ClusterCountPillOptions {
  iconSize: number;
  compact?: boolean;
}

function drawRoundedPillPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  const radius = height / DIVISION_FACTOR;
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

export interface ClusterPillRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function resolveClusterPillLabel(
  count: number,
  compact: boolean | undefined
): string {
  if (compact) {
    return '+';
  }
  if (count > MAP_CONSTANTS.BADGE_COUNT_CAP) {
    return `${String(MAP_CONSTANTS.BADGE_COUNT_CAP)}+`;
  }
  return String(count);
}

/**
 * Pure geometry for the cluster count pill. Returns `null` when no pill would
 * be drawn (count <= 1). `ctx` is only used for `measureText` and is restored
 * to its prior state via save/restore so callers' draw state is preserved.
 *
 * Shared by `drawClusterCountPill` and pill hit-testing so the two cannot drift.
 */
export function getClusterPillRect(
  ctx: CanvasRenderingContext2D,
  position: { x: number; y: number },
  count: number,
  options: ClusterCountPillOptions
): ClusterPillRect | null {
  if (count <= 1) {
    return null;
  }

  const label = resolveClusterPillLabel(count, options.compact);
  const iconVisualSize = options.iconSize * SCALE_FACTOR;
  const top =
    position.y +
    iconVisualSize / DIVISION_FACTOR +
    CLUSTER_PILL_OFFSET_FROM_ICON;

  ctx.save();
  ctx.font = `bold ${String(CLUSTER_PILL_FONT_SIZE)}px system-ui, sans-serif`;
  const textWidth = ctx.measureText(label).width;
  ctx.restore();

  const width = textWidth + CLUSTER_PILL_PADDING_X * 2;
  const left = position.x - width / DIVISION_FACTOR;

  return { left, top, width, height: CLUSTER_PILL_HEIGHT };
}

/**
 * Draw a count pill below a clustered standalone icon representative.
 */
export function drawClusterCountPill(
  ctx: CanvasRenderingContext2D,
  position: { x: number; y: number },
  count: number,
  options: ClusterCountPillOptions
): void {
  const rect = getClusterPillRect(ctx, position, count, options);
  if (!rect) {
    return;
  }

  const label = resolveClusterPillLabel(count, options.compact);
  const textCenterY = rect.top + rect.height / DIVISION_FACTOR;

  ctx.save();
  ctx.font = `bold ${String(CLUSTER_PILL_FONT_SIZE)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 1;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 2;

  drawRoundedPillPath(ctx, rect.left, rect.top, rect.width, rect.height);
  ctx.fillStyle = '#217CBB';
  ctx.fill();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(label, position.x, textCenterY);
  ctx.restore();
}
